<template>
  <div class="member-manage">
    <div v-if="!pm.canModifyProfile && !pm.userId" class="alert alert-primary alert-sm">
      This profile is managed by someone else.
    </div>
    <div class="share-profile">
      <div class="form-check form-switch">
        <label for="shareProfileOption" class="form-check-label">
          Profile shared with group
          <img v-if="pm.lastSharingInvite?.isPending" src="@/assets/icons/mail_gray_60.png" class="invited">
        </label>
        <input type="checkbox"
          id="shareProfileOption"
          :checked="pm.isProfileShared"
          :disabled="busyRef || !pm.canModifyProfile || pm.hasPendingInvite"
          class="form-check-input"
          @change="toggleShareProfile">
        <template v-if="canEditGroupRef && !pm.canModifyProfile">
          <button type="button" v-if="pm.isProfileShared" class="btn btn-inline btn-link" @click="emit('removeProfile', pm.profileGroupId!)">
            Remove
          </button>
          <button type="button" v-else-if="!pm.hasPendingInvite" class="btn btn-inline btn-link" @click="emit('shareProfile', pm.profileGroupId!)">
            Request
          </button>
        </template>
      </div>
      <div class="help-text-sm">
        Ensure other group members can see this person in their family tree.
      </div>
    </div>
    <div v-if="pm.member || pm.hasPendingGroupInvite">
      <label class="form-label">
        Role
      </label>
      <div class="member-role">
        {{ memDisplayRoleRef }}
        <img v-if="pm.hasPendingGroupInvite" src="@/assets/icons/mail_gray_60.png" class="invited">
        <button type="button" v-else-if="canChangeRoleRef" class="btn btn-inline btn-link" @click="emit('removeUser', pm)">
          <template v-if="memUserIsSelfRef">
            Leave group
          </template>
          <template v-else>
            Remove
          </template>
        </button>
      </div>
      <div class="help-text-sm">
        {{ memRoleInfoRef }}
      </div>
    </div>
    <div v-if="pm.member && !memIsGroupOwnerRef" class="can-invite">
      <div class="form-check form-switch">
        <label for="canInviteOption" class="form-check-label">
          Can invite others
        </label>
        <input type="checkbox"
          id="canInviteOption"
          v-model="memCanInviteRef"
          :disabled="busyRef || !canChangeRoleRef"
          class="form-check-input">
      </div>
      <div class="help-text-sm">
        Allow the group member to share profiles with the group and invite other people to join.
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.member-manage {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .alert {
    margin: 0 -1rem;
  }
}

.input-value {
  margin: 0;
}

.share-profile .form-check-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .invited {
    width: 1.2rem;
    filter: brightness(0) saturate(100%) invert(63%) sepia(76%) saturate(5097%) hue-rotate(184deg) brightness(100%) contrast(94%);
  }
}

.member-role {
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  gap: 0.5rem;
  align-items: center;
  justify-items: flex-start;
  transform: translateY(-2px);

  .invited {
    width: 1.2rem;
    filter: brightness(0) saturate(100%) invert(63%) sepia(76%) saturate(5097%) hue-rotate(184deg) brightness(100%) contrast(94%);
  }

  .btn-inline {
    margin-left: auto;
    font-size: 0.875rem;
  }
}

.member-action {
  font-size: 0.875rem;
}
</style>

<script lang="ts" setup>
import { computed, nextTick, reactive, ref, watch } from 'vue'
import { PotentialMember, useFamilyGroupMemberManager } from './FamilyGroupMemberManager'
import { FamilyGroupMember, GroupMemberRole, ItemPermissions } from '@/gp/GroupAdminModel'
import { useFamilyGroupStore } from '@/gp/FamilyGroupStore'
import { useFamilyGroupMemberStore } from '@/gp/FamilyGroupMemberStore'
import { useInstantSave } from '@/util/InstantSave'
import { PatchChange } from '@/util/Api'
import { useTokenManager } from '@/auth/TokenManager'

const props = defineProps<{
  familyGroupId: string
  pm: PotentialMember
  showActions?: boolean
}>()

const emit = defineEmits<{
  (e: 'shareProfile', profileGroupId: string): void
  (e: 'removeProfile', profileGroupId: string): void
  (e: 'removeUser', pm: PotentialMember): void
}>()

const canInviteRoles = [
  GroupMemberRole.Owner,
  GroupMemberRole.Administrator,
  GroupMemberRole.Recruiter,
]

let ignoreChanges = false

const busyRef = ref(false)
const editMember = reactive(new FamilyGroupMember())

const familyGroupStore = useFamilyGroupStore()
const memberStore = useFamilyGroupMemberStore()
const memberManager = useFamilyGroupMemberManager()
const tokenManager = useTokenManager()

const memberRef = computed(() => props.pm.member)
const memDisplayRoleRef = computed<GroupMemberRole>({
  get: () => editMember.role == GroupMemberRole.Recruiter ? GroupMemberRole.Member : editMember.role,
  set: value => {
    if (value == GroupMemberRole.Member) {
      editMember.role = memCanInviteRef.value ? GroupMemberRole.Recruiter : GroupMemberRole.Member
    }
    else {
      editMember.role = value
    }
  }
})
const memCanInviteRef = computed({
  get: () => canInviteRoles.includes(editMember.role),
  set: value => {
    if (memDisplayRoleRef.value == GroupMemberRole.Member) {
      editMember.role = value ? GroupMemberRole.Recruiter : GroupMemberRole.Member
    }
  }
})

useInstantSave(memberRef, editMember, ['role'], saveMemberChangesAsync)

const memUserIsSelfRef = computed(() => props.pm.userId == tokenManager.current.userId)
const canEditUserRef = computed(() => memUserIsSelfRef.value || props.pm.isUserSupervisor)
const memIsGroupOwnerRef = computed(() => props.pm.member?.role == GroupMemberRole.Owner)

const groupPermissionsRef = computed(() => familyGroupStore.getLoadedPermissions(props.familyGroupId)) // loaded by parent
const canEditGroupRef = computed(() => groupPermissionsRef.value & ItemPermissions.Modify)
const canChangeRoleRef = computed(() => canEditGroupRef.value && !memIsGroupOwnerRef.value)

const memRoleInfoRef = computed(() => {
  switch (memDisplayRoleRef.value) {
    case GroupMemberRole.Owner:
      return "An owner can view and manage all aspects of the group."
    case GroupMemberRole.Administrator:
      return "An administrator can view and manage all aspects of the group, except changing the owner or deleting the group."
    case GroupMemberRole.Recruiter:
    case GroupMemberRole.Member:
      return "A member can see other members and any profiles and trees shared with the group."
    case GroupMemberRole.Viewer:
      return "A viewer can see the group itself, but not the group members, shared profiles or shared trees."
    default:
      return undefined
  }
})

watch(() => props.pm.member, async () => {
  if (props.pm.member) {
    ignoreChanges = true

    Object.assign(editMember, props.pm.member)

    await nextTick() // allow reactive effects to execute
    ignoreChanges = false
  }
}, { deep: true })

async function toggleShareProfile(e: Event) {
  const checkElem = e.target as HTMLInputElement
  if (checkElem.checked) {
    await memberManager.shareProfilesAsync(props.familyGroupId, [props.pm.profileGroupId!])
  }
  else {
    await memberManager.removeSharedProfilesAsync(props.familyGroupId, [props.pm.profileGroupId!])
  }
}

async function saveMemberChangesAsync(changes: PatchChange[]) {
  try {
    if (!ignoreChanges) {
      busyRef.value = true
      await memberStore.patchAsync(props.pm.member!.key!, changes)
    }
  }
  finally {
    busyRef.value = false
  }
}
</script>
